<script setup lang="ts">
import { nextTick, onMounted, useTemplateRef } from 'vue'
import { useRouter } from 'vue-router'
import DreamaProgressiveImage from '@/components/dreama-progressive-image'
import { stateImages } from '@/utils/state-images'

defineOptions({
  name: 'NotFoundView',
})

const router = useRouter()
const ctaRef = useTemplateRef<HTMLButtonElement>('ctaRef')

function backToFeed() {
  void router.replace('/feed')
}

onMounted(() => {
  void nextTick(() => ctaRef.value?.focus())
})
</script>

<template>
  <section class="not-found-page" aria-labelledby="not-found-title">
    <div class="not-found-page__content">
      <div class="not-found-page__illustration">
        <DreamaProgressiveImage
          :src="stateImages.notFound"
          alt="没有找到这个页面的插画"
          aspect-ratio="1 / 1"
          object-fit="contain"
          priority
          :preview-width="48"
          :preview-height="48"
          :request-width="320"
          :request-height="320"
        />
      </div>

      <h1 id="not-found-title" class="not-found-page__title">咦，没有找到这个页面</h1>

      <button ref="ctaRef" type="button" class="not-found-page__button" @click="backToFeed">
        回到发现页
      </button>
    </div>
  </section>
</template>

<style scoped>
.not-found-page {
  display: flex;
  min-height: calc(100vh - 96px);
  align-items: center;
  justify-content: center;
  background: var(--app-bg);
  color: var(--app-text-main);
}

.not-found-page__content {
  display: flex;
  width: min(100%, 600px);
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 18px;
  padding: 24px 16px 40px;
  text-align: center;
  animation: not-found-enter 0.26s ease both;
}

.not-found-page__illustration {
  width: clamp(var(--app-state-illustration-size), 16vw, var(--app-state-illustration-size-large));
  height: clamp(var(--app-state-illustration-size), 16vw, var(--app-state-illustration-size-large));
  --dreama-image-placeholder-bg: transparent;
}

.not-found-page__title {
  margin: 0;
  color: var(--app-text-main);
  font-size: 18px;
  font-weight: 600;
  line-height: 24px;
}

.not-found-page__button {
  display: inline-flex;
  min-width: 128px;
  min-height: 40px;
  align-items: center;
  justify-content: center;
  padding: 8px 18px;
  border: 0;
  border-radius: 24px;
  appearance: none;
  background: var(--app-text-main);
  color: var(--app-bg);
  cursor: pointer;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  line-height: 20px;
  outline: none;
  transition:
    opacity var(--app-interaction-duration) var(--app-interaction-ease),
    transform var(--app-interaction-duration) var(--app-interaction-ease);
}

.not-found-page__button:hover,
.not-found-page__button:focus-visible {
  opacity: 0.86;
}

.not-found-page__button:focus-visible {
  box-shadow:
    0 0 0 3px var(--app-bg),
    0 0 0 5px var(--app-text-main);
}

.not-found-page__button:active {
  transform: scale(var(--app-interaction-standard-active-scale));
}

@keyframes not-found-enter {
  from {
    opacity: 0;
    transform: translateY(6px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .not-found-page__content {
    gap: 16px;
    padding: 20px 14px 34px;
  }

  .not-found-page__illustration {
    width: clamp(140px, 44vw, var(--app-state-illustration-size));
    height: clamp(140px, 44vw, var(--app-state-illustration-size));
  }
}

@media (prefers-reduced-motion: reduce) {
  .not-found-page__content {
    animation: none;
  }

  .not-found-page__button {
    transition: none;
  }
}
</style>
